import { ComponentProps, useEffect, useMemo, useRef } from "react";

import { Upload } from "lucide-react";

import { useBundleSplitterTasks } from "@/hooks/queries/use-bundle-splitter";
import { useCaseByCaseNumber } from "@/hooks/queries/use-cases";
import { useAuth } from "@/hooks/useAuth";

import { useToast } from "@components/PersistentToast/hooks/useToast";
import { Button } from "@components/ui/button";

import { buildCaseSubtitle } from "@pages/ReportStudio/CaseDetail/build-case-subtitle";

import {
  forgetDismissedBundleTaskIds,
  getDismissedBundleTaskIds,
} from "@utils/dismissedBundleTasks";

import { BundleUploadPanel } from "./BundleUpload/BundleUploadPanel";
import {
  RehydratedTask,
  selectShouldMinimise,
  useBundleUploads,
} from "./BundleUpload/store/useBundleUploads";
import { bundleToastTitle } from "./BundleUpload/utils/bundleToastTitle";
import { extractUserId } from "./BundleUpload/utils/extractUserId";
import { TASK_IN_FLIGHT_STATUSES } from "./BundleUpload/utils/uploadConflicts";

type UploadBundleFileProps = {
  caseNumber?: string;
  onToastOpen?: () => void;
  onToastClose?: () => void;
  /** Button style; primary by default. The case detail page makes it secondary. */
  variant?: ComponentProps<typeof Button>["variant"];
};

export const UploadBundleFile = ({
  caseNumber = "",
  onToastOpen,
  onToastClose,
  variant,
}: UploadBundleFileProps) => {
  const { persistentToast } = useToast();
  const { user } = useAuth();
  const openPicker = useBundleUploads((state) => state.openPicker);
  const addRehydrated = useBundleUploads((state) => state.addRehydrated);
  const setActiveTarget = useBundleUploads((state) => state.setActiveTarget);
  const clearActiveTarget = useBundleUploads((state) => state.clearActiveTarget);
  const userId = extractUserId(user);

  // MDRS-550: rows from several cases share the pop-up, so each names its case
  // the way the case page header does. Already cached by the case shell.
  const { data: caseData } = useCaseByCaseNumber({
    case_number: caseNumber,
    enabled: !!caseNumber,
  });
  const caseLabel =
    (caseData &&
      buildCaseSubtitle({
        examineeName: caseData.examinee_name,
        claimNumber: caseData.claim_number,
      })) ||
    undefined;
  const target = { caseNumber, userId, caseLabel };

  // MDRS-550: while this case page is on screen the picker uploads to it, so
  // a picker left open never sends files to a case the user navigated away
  // from. Leaving hides the picker (and the panel closes itself if that
  // leaves it empty).
  useEffect(() => {
    setActiveTarget({ caseNumber, userId, caseLabel });
    return () => clearActiveTarget(caseNumber);
  }, [caseNumber, userId, caseLabel, setActiveTarget, clearActiveTarget]);

  // STP-77: on mount, fetch in-flight bundle-splitter tasks for this case so
  // that a refresh / re-login can resume an existing job instead of orphaning it.
  const { data: tasksData } = useBundleSplitterTasks({
    case_number: caseNumber || undefined,
    enabled: !!caseNumber,
  });

  const rehydratedTasks = useMemo<RehydratedTask[]>(() => {
    if (!tasksData?.tasks) return [];
    // Forget dismissed task_ids this case's list reports as ended (they're
    // never recovered again), then exclude the ones still dismissed from the
    // rehydration list. Only ended tasks: another case's dismissals aren't in
    // this list at all, and must survive it (MDRS-550).
    forgetDismissedBundleTaskIds(
      tasksData.tasks
        .filter((t) => !TASK_IN_FLIGHT_STATUSES.has(t.status))
        .map((t) => t.task_id)
    );
    const dismissed = getDismissedBundleTaskIds();
    return tasksData.tasks
      .filter((t) => TASK_IN_FLIGHT_STATUSES.has(t.status))
      .filter((t) => !dismissed.has(t.task_id))
      .flatMap((t) => {
        // STP-86: stale tasks can come back with no file metadata; rehydrating
        // them surfaces a blank row and looks like an unprompted modal.
        const fileName = t.file_names?.[0]?.trim();
        if (!fileName) return [];
        return [
          {
            taskId: t.task_id,
            fileName,
            fileSize: t.file_size ?? undefined,
            phase: "polling" as const,
          },
        ];
      });
  }, [tasksData]);

  // MDRS-550: every case's rows share one toast. Showing it again with the
  // same panel keeps the rows already mounted (and their in-flight work) —
  // it only refreshes the header and expands it.
  const showUploadToast = () => {
    const uploads = useBundleUploads.getState();
    persistentToast({
      title: bundleToastTitle(uploads.rows),
      content: <BundleUploadPanel />,
      minimiseOnDismiss: selectShouldMinimise(uploads),
      onClose: () => {
        uploads.clear();
        onToastClose?.();
      },
    });

    // Notify parent that toast is open
    onToastOpen?.();
  };

  const handleOpenUploadDialog = () => {
    openPicker(target);
    showUploadToast();
  };

  // Case recovery last ran for: moving straight between two cases can keep
  // this button mounted and only change its case (MDRS-550).
  const rehydratedCaseRef = useRef<string | null>(null);

  useEffect(() => {
    if (rehydratedCaseRef.current === caseNumber) return;
    if (!caseNumber) return;
    if (rehydratedTasks.length === 0) return;

    rehydratedCaseRef.current = caseNumber;
    // Already-listed tasks (this page opened again) don't re-pop the toast.
    if (addRehydrated(target, rehydratedTasks) === 0) return;
    showUploadToast();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseNumber, rehydratedTasks]);

  return (
    <Button variant={variant} size="sm" onClick={handleOpenUploadDialog}>
      <Upload size={16} />
      Upload case bundle
    </Button>
  );
};
